import React, { useState, useEffect } from 'react';

interface ViewportTest {
  name: string;
  width: number;
  height: number;
  description: string;
}

interface ValidationResult {
  viewport: string;
  tests: {
    navigation: boolean;
    layout: boolean;
    interactions: boolean;
    performance: boolean;
  };
  issues: string[];
}

/**
 * Integration validator for testing app functionality across different viewports
 * Simulates different device sizes and validates core functionality
 */
export const IntegrationValidator: React.FC = () => {
  const [currentTest, setCurrentTest] = useState<ViewportTest | null>(null);
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const viewportTests: ViewportTest[] = [
    {
      name: 'iPhone SE',
      width: 375,
      height: 667,
      description: 'Минимальный размер современного смартфона'
    },
    {
      name: 'iPhone 12/13/14',
      width: 390,
      height: 844,
      description: 'Стандартный размер iPhone'
    },
    {
      name: 'iPhone 12/13/14 Pro Max',
      width: 428,
      height: 926,
      description: 'Большой iPhone'
    },
    {
      name: 'Samsung Galaxy S21',
      width: 360,
      height: 800,
      description: 'Стандартный Android смартфон'
    },
    {
      name: 'iPad Mini',
      width: 768,
      height: 1024,
      description: 'Планшет (не основная цель, но должен работать)'
    }
  ];

  const runViewportTest = async (viewport: ViewportTest): Promise<ValidationResult> => {
    setCurrentTest(viewport);
    
    // Simulate viewport change
    const originalWidth = window.innerWidth;
    const originalHeight = window.innerHeight;
    
    // We can't actually resize the window in a web app, but we can test responsive classes
    const result: ValidationResult = {
      viewport: viewport.name,
      tests: {
        navigation: true,
        layout: true,
        interactions: true,
        performance: true
      },
      issues: []
    };

    // Test 1: Navigation visibility and accessibility
    try {
      const navElements = document.querySelectorAll('[role="navigation"], nav, .navigation');
      if (navElements.length === 0) {
        result.tests.navigation = false;
        result.issues.push('Навигация не найдена');
      }

      // Check if navigation is accessible on small screens
      if (viewport.width < 400) {
        const mobileNavElements = document.querySelectorAll('.sm\\:hidden, .md\\:hidden');
        if (mobileNavElements.length === 0) {
          result.issues.push('Мобильная навигация может быть не оптимизирована');
        }
      }
    } catch (error) {
      result.tests.navigation = false;
      result.issues.push('Ошибка при тестировании навигации');
    }

    // Test 2: Layout responsiveness
    try {
      const containers = document.querySelectorAll('.container, .max-w-');
      const cards = document.querySelectorAll('.card, .bg-tg-secondary');
      
      if (containers.length === 0) {
        result.issues.push('Контейнеры не найдены');
      }

      if (cards.length === 0) {
        result.issues.push('Карточки интерфейса не найдены');
      }

      // Check for horizontal scrolling (bad on mobile)
      if (document.body.scrollWidth > viewport.width + 10) {
        result.tests.layout = false;
        result.issues.push('Обнаружена горизонтальная прокрутка');
      }
    } catch (error) {
      result.tests.layout = false;
      result.issues.push('Ошибка при тестировании макета');
    }

    // Test 3: Interactive elements
    try {
      const buttons = document.querySelectorAll('button, .btn-primary, .btn-secondary');
      const inputs = document.querySelectorAll('input, textarea, select');
      
      if (buttons.length === 0) {
        result.tests.interactions = false;
        result.issues.push('Кнопки не найдены');
      }

      // Check button sizes for touch targets (minimum 44px)
      buttons.forEach((button, index) => {
        const rect = button.getBoundingClientRect();
        if (rect.height < 44 || rect.width < 44) {
          result.issues.push(`Кнопка ${index + 1} слишком мала для сенсорного ввода`);
        }
      });

      // Check input accessibility
      inputs.forEach((input, index) => {
        const rect = input.getBoundingClientRect();
        if (rect.height < 44) {
          result.issues.push(`Поле ввода ${index + 1} слишком мало для сенсорного ввода`);
        }
      });
    } catch (error) {
      result.tests.interactions = false;
      result.issues.push('Ошибка при тестировании интерактивных элементов');
    }

    // Test 4: Performance considerations
    try {
      const images = document.querySelectorAll('img');
      const heavyElements = document.querySelectorAll('.animate-spin, .transition-all');
      
      // Check for unoptimized images on small screens
      if (viewport.width < 400 && images.length > 10) {
        result.issues.push('Много изображений на мобильном устройстве');
      }

      // Check for excessive animations on low-end devices
      if (viewport.width < 400 && heavyElements.length > 20) {
        result.issues.push('Много анимаций для мобильного устройства');
      }

      // Check DOM complexity
      const allElements = document.querySelectorAll('*');
      if (allElements.length > 1000) {
        result.issues.push('Сложная DOM структура может влиять на производительность');
      }
    } catch (error) {
      result.tests.performance = false;
      result.issues.push('Ошибка при тестировании производительности');
    }

    // Small delay to simulate testing time
    await new Promise(resolve => setTimeout(resolve, 1000));

    return result;
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setResults([]);
    
    for (const viewport of viewportTests) {
      const result = await runViewportTest(viewport);
      setResults(prev => [...prev, result]);
    }
    
    setCurrentTest(null);
    setIsRunning(false);
  };

  const getOverallScore = () => {
    if (results.length === 0) return 0;
    
    const totalTests = results.length * 4; // 4 tests per viewport
    const passedTests = results.reduce((sum, result) => {
      return sum + Object.values(result.tests).filter(Boolean).length;
    }, 0);
    
    return Math.round((passedTests / totalTests) * 100);
  };

  const getTotalIssues = () => {
    return results.reduce((sum, result) => sum + result.issues.length, 0);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">📱 Интеграционное тестирование</h1>
        <p className="text-tg-secondary">
          Проверка работы приложения на разных устройствах
        </p>
      </div>

      {/* Test Controls */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Управление тестами</h2>
          <button
            onClick={runAllTests}
            disabled={isRunning}
            className="btn-primary"
          >
            {isRunning ? 'Тестирование...' : 'Запустить все тесты'}
          </button>
        </div>
        
        {currentTest && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="font-medium">Тестирование: {currentTest.name}</span>
            </div>
            <p className="text-sm text-blue-600 mt-1">{currentTest.description}</p>
          </div>
        )}
      </div>

      {/* Results Summary */}
      {results.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Сводка результатов</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{getOverallScore()}%</div>
              <div className="text-sm text-tg-secondary">Общий балл</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{results.length}</div>
              <div className="text-sm text-tg-secondary">Устройств протестировано</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{getTotalIssues()}</div>
              <div className="text-sm text-tg-secondary">Найдено проблем</div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Детальные результаты</h2>
          {results.map((result, index) => (
            <div key={index} className="card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold">{result.viewport}</h3>
                  <p className="text-sm text-tg-secondary">
                    {viewportTests.find(v => v.name === result.viewport)?.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">
                    {Object.values(result.tests).filter(Boolean).length}/4
                  </div>
                  <div className="text-sm text-tg-secondary">тестов пройдено</div>
                </div>
              </div>

              {/* Test Results Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`p-2 rounded ${result.tests.navigation ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <div className="font-medium">
                    {result.tests.navigation ? '✅' : '❌'} Навигация
                  </div>
                </div>
                <div className={`p-2 rounded ${result.tests.layout ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <div className="font-medium">
                    {result.tests.layout ? '✅' : '❌'} Макет
                  </div>
                </div>
                <div className={`p-2 rounded ${result.tests.interactions ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <div className="font-medium">
                    {result.tests.interactions ? '✅' : '❌'} Взаимодействие
                  </div>
                </div>
                <div className={`p-2 rounded ${result.tests.performance ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <div className="font-medium">
                    {result.tests.performance ? '✅' : '❌'} Производительность
                  </div>
                </div>
              </div>

              {/* Issues */}
              {result.issues.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-600 mb-2">Обнаруженные проблемы:</h4>
                  <ul className="space-y-1">
                    {result.issues.map((issue, issueIndex) => (
                      <li key={issueIndex} className="text-sm text-red-600 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Testing Guidelines */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold mb-4">Рекомендации по тестированию</h2>
        <div className="space-y-3 text-sm">
          <div>
            <strong>Целевые устройства:</strong>
            <p className="text-tg-secondary mt-1">
              Приложение оптимизировано для смартфонов с экранами от 375px до 428px в ширину.
              Планшеты поддерживаются, но не являются основной целью.
            </p>
          </div>
          
          <div>
            <strong>Критерии успеха:</strong>
            <ul className="list-disc list-inside ml-4 mt-1 text-tg-secondary">
              <li>Все элементы навигации доступны и видны</li>
              <li>Отсутствует горизонтальная прокрутка</li>
              <li>Сенсорные цели не менее 44px</li>
              <li>Быстрая загрузка и отзывчивый интерфейс</li>
            </ul>
          </div>
          
          <div>
            <strong>Дополнительное тестирование:</strong>
            <ul className="list-disc list-inside ml-4 mt-1 text-tg-secondary">
              <li>Тестируйте в реальном Telegram на разных устройствах</li>
              <li>Проверьте работу в режиме разработчика браузера</li>
              <li>Убедитесь в корректной работе сенсорных жестов</li>
              <li>Проверьте производительность на слабых устройствах</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationValidator;